import { Form, useNavigate, useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { FormikProvider } from 'formik'
import {
  ContactFragment,
  FinanceDealsSelectorFragment,
  LegalEntityForSelectFragment,
  QuoteDetailsPageDocument,
  QuotesTabsVisibilityDocument,
  SalesDocumentTemplateForSelectFragment,
  UpdateQuoteDetailsDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import CurrencySelector from 'modules/finance/components/CurrencySelector'
import { FinanceDealsSelector } from 'modules/finance/components/Deals/FinanceDealsSelector'
import { SectionTitle } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/_SectionTitle'
import { getDefaultSalesDocumentTemplate } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/defaultValuesUtils'
import { FinanceSalesDocumentTemplateSelector } from 'modules/finance/components/SalesDocumentTemplates/FinanceSalesDocumentTemplateSelector'
import { useConvertQuoteIntoInvoice } from 'modules/finance/hooks/quotes/useConvertQuoteIntoInvoice'
import { useCreateProjectFromQuote } from 'modules/finance/hooks/quotes/useCreateProjectFromQuote'
import { useDownloadQuotePdf } from 'modules/finance/hooks/quotes/useDownloadQuotePdf'
import { onHeaderActionClick } from 'modules/finance/lib/quotesUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const extractDateValue = (date: string | undefined | null) =>
  date !== undefined && date !== '' && date !== null ? dateBackendToUI(date) : ''

const QuoteDetailsPage = () => {
  const { quoteId } = useParams()
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const downloadQuotePdf = useDownloadQuotePdf()
  const convertQuoteIntoInvoice = useConvertQuoteIntoInvoice({
    refetchQueries: [QuotesTabsVisibilityDocument],
  })
  const createProjectFromQuote = useCreateProjectFromQuote({
    refetchQueries: [QuotesTabsVisibilityDocument],
  })
  const navigateToProject = () => {
    quote?.projectId &&
      navigate(
        navigation.projects.id.details.overview.fullPath({
          id: String(quote.projectId),
        })
      )
  }
  const navigateToInvoice = () => {
    quoteFinancialDocumentId &&
      navigate(
        navigation.finance.sales.salesInvoices.detail.fullPath({
          invoiceId: Number(quoteFinancialDocumentId),
        })
      )
  }

  const { data, error, loading } = useQuery(QuoteDetailsPageDocument, {
    variables: {
      id: quoteId ? Number(quoteId) : -1,
    },
    skip: !quoteId,
  })
  const quote = data?.finance.quote
  const quoteFinancialDocumentId = quote?.financialDocumentAssociation?.financialDocument?.id
  const authorized = quote?._policies?.updateDetails.can
  const canSeeDeals = data?.finance?._policies?.sidebarSeeFinanceDealsSubLevelItem.can
  const defaultSalesDocumentTemplates = data?.finance.defaultSalesDocumentTemplates ?? []
  const defaultSalesDocumentTemplate = getDefaultSalesDocumentTemplate(
    quote?.legalEntity,
    defaultSalesDocumentTemplates
  )
  const editable = quote?.status == 'draft'

  const [updateQuoteDetails, {}] = useMutation(UpdateQuoteDetailsDocument, {
    extractorFn: (data) => data.financeMutations.updateDetailsFinanceQuote,
    refetchQueries: [],
    awaitRefetchQueries: true,
  })

  const onSubmit = async (values: QuoteFormFields) => {
    if (
      !values.currency ||
      !values.quoteNumber ||
      !values.legalEntity ||
      !values.contact ||
      !values.documentDate
    ) {
      return { success: false, data: {}, errors: [] }
    }

    const result = await updateQuoteDetails({
      variables: {
        id: Number(quoteId),
        dealId: values.deal?.id,
        currency: values.currency,
        quoteNumber: values.quoteNumber,
        expirationDate: values.expirationDate,
        documentDate: values.documentDate,
        legalEntityId: values.legalEntity?.id,
        contactId: values.contact?.id,
        memo: values.memo,
        footer: values.footer,
      },
    })

    const handled = handleResult(result, (errors) => {
      form.setErrors({ ...errors })
    })
    if (handled.success) {
      track(trackingEvents.accounting.quotes.submitEdit)

      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.quotes.notifications.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
    return handled
  }

  const validateForm = (data: QuoteFormFields) => {
    const errors: FormErrors<QuoteFormFields> = {}

    if (!data.quoteNumber) {
      errors.quoteNumber = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!data.documentDate) {
      errors.documentDate = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!data.legalEntity) {
      errors.legalEntity = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!data.contact) {
      errors.contact = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!data.currency) {
      errors.currency = i18n.t('finance.sale_invoices.form.error_required')
    }

    return errors
  }

  interface QuoteFormFields {
    currency?: string | null
    quoteNumber?: string | null
    expirationDate?: string | null
    documentDate?: string | null
    legalEntity?: LegalEntityForSelectFragment | null //issuer
    contact?: ContactFragment | null //receiver / recipient
    deal?: FinanceDealsSelectorFragment | null
    memo?: string | null
    footer?: string | null
    salesDocumentTemplate?: SalesDocumentTemplateForSelectFragment | null
  }

  const form = useForm<QuoteFormFields>({
    enableReinitialize: true,
    defaultValues: {
      currency: quote?.currency,
      quoteNumber: quote?.quoteNumber,
      documentDate: quote?.documentDate,
      expirationDate: quote?.expirationDate,
      legalEntity: quote?.legalEntity,
      contact: quote?.client,
      deal: quote?.deal,
      memo: quote?.memo,
      footer: quote?.footer,
      salesDocumentTemplate: defaultSalesDocumentTemplate,
    },
    validate: validateForm,
    validateOnChange: false,
    onSubmit: async (data) => {
      return await onSubmit(data)
    },
  })

  const minDate = moment().subtract(180, 'days')
  const maxDate = moment().add(5, 'years')

  const handleContactSelect = (contact: ContactFragment | null) => {
    // Deal client and contact id must be the same, to avoid issues we reset the selected deal if they are different
    const dealClientId = form.values.deal?.client?.id
    const contactId = contact?.id

    if (dealClientId === contactId) {
      form.setValues({ ...form.values, contact: contact ?? undefined })
    } else {
      form.setValues({ ...form.values, contact: contact ?? undefined, deal: undefined })
    }
  }

  const handleDealSelect = (deal: FinanceDealsSelectorFragment | null) => {
    // Deal client and contact id must be the same, to avoid issues we reset the selected contact if they are different
    // If no contact is selected, we set the deal and the contact to the deal client

    const contactId = form.values.contact?.id
    const dealClientId = deal?.client?.id

    if (!contactId) {
      form.setValues({
        ...form.values,
        deal: deal ?? undefined,
        contact: deal?.client ?? undefined,
      })
    } else if (dealClientId === contactId) {
      form.setValues({ ...form.values, deal: deal ?? undefined })
    } else {
      form.setValues({ ...form.values, deal: deal ?? undefined, contact: undefined })
    }
  }

  const handleSalesDocumentTemplateSelect = (
    salesDocumentTemplate: SalesDocumentTemplateForSelectFragment | null
  ) => {
    form.setValues({
      ...form.values,
      salesDocumentTemplate: salesDocumentTemplate,
      footer: salesDocumentTemplate?.footer ?? undefined,
    })
  }

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={authorized}
      loading={loading}
      error={error}
      onActionClick={(key) =>
        onHeaderActionClick(
          key,
          Number(quoteId),
          createProjectFromQuote,
          convertQuoteIntoInvoice,
          downloadQuotePdf,
          navigateToProject,
          navigateToInvoice
        )
      }
    >
      <TwoColumnLayout sideContent={<></>}>
        <FormikProvider value={form}>
          <Form>
            <Box paddingTop="s16" paddingBottom="s16" gap="s16">
              <SectionTitle
                title={i18n.t('finance.quotes.form.title')}
                description={i18n.t('finance.quotes.form.description')}
              />
              <Box
                width="full"
                flexDirection="row"
                gap="s16"
                style={{
                  width: '50%',
                  paddingRight: '8px',
                }}
              >
                <FinanceSalesDocumentTemplateSelector
                  label={i18n.t('finance.sale_invoices.form.template.title')}
                  emptyLabel={i18n.t('finance.sale_invoices.form.template.select_a_template')}
                  value={form.values.salesDocumentTemplate ?? null}
                  onSelect={handleSalesDocumentTemplateSelect}
                  queryParams={{
                    legalEntityIds: form.values.legalEntity?.id
                      ? [form.values.legalEntity.id]
                      : undefined,
                  }}
                  disableCreate
                  hideSearchIcon
                  disabled={!editable}
                />
              </Box>
            </Box>
            <Box
              width="full"
              flexDirection="row"
              paddingBottom="s16"
              gap="s16"
              style={{
                width: '50%',
                paddingRight: '8px',
              }}
            >
              <TextField
                label={i18n.t('finance.quotes.form.quote_number')}
                value={form.values.quoteNumber || ''}
                onChange={(value) => form.setValues({ ...form.values, quoteNumber: value })}
                error={form.errors.quoteNumber}
                placeholder={i18n.t('finance.quotes.form.quote_number_placeholder')}
                readonly={!editable}
              />
            </Box>
            <Box flexDirection="row" gap="s16" paddingBottom="s16">
              <DayPicker
                label={i18n.t('finance.quotes.form.quote_date')}
                value={extractDateValue(form.values.documentDate)}
                onChange={(value) =>
                  form.setValues({ ...form.values, documentDate: dateUIToBackend(value) })
                }
                error={form.errors.documentDate}
                minDate={minDate}
                maxDate={form.values.expirationDate ? moment(form.values.expirationDate) : maxDate}
                readonly={!editable}
              />
              <DayPicker
                label={i18n.t('finance.quotes.form.expiration_date')}
                value={extractDateValue(form.values.expirationDate)}
                onChange={(value) =>
                  form.setValues({ ...form.values, expirationDate: dateUIToBackend(value) })
                }
                error={form.errors.expirationDate}
                minDate={form.values.documentDate ? moment(form.values.documentDate) : minDate}
                maxDate={maxDate}
                readonly={!editable}
              />
            </Box>
            <Box width="full" flexDirection="row" gap="s16" paddingBottom="s16">
              <LegalEntitySelector
                label={i18n.t('finance.sale_invoices.form.from_to')}
                width="full"
                value={form.values.legalEntity ?? null}
                onChange={(value) =>
                  form.setValues({
                    ...form.values,
                    legalEntity: value,
                    currency: form.values.currency || value?.currency,

                    salesDocumentTemplate: undefined,
                  })
                }
                error={form.errors.legalEntity}
                hideSearchIcon
                disabled={!editable}
              />
              <FinanceContactsSelector
                value={form.values.contact}
                onSelect={handleContactSelect}
                label={i18n.t('finance.sale_invoices.form.bill_to')}
                type="finance_contact"
                queryParams={{
                  companyId: company.id,
                  contactCreateType: 'client',
                }}
                error={form.errors.contact}
                hideSearchIcon
                disabled={!editable}
              />
            </Box>

            {canSeeDeals && (
              <Box width="full" flexDirection="row" gap="s16" paddingBottom="s16">
                <FinanceDealsSelector
                  value={form.values.deal}
                  onSelect={handleDealSelect}
                  label={i18n.t('finance.sale_invoices.form.deal')}
                  type="finance_deal"
                  queryParams={{
                    clientIds: form.values.contact?.id ? [form.values.contact?.id] : undefined,
                  }}
                  excludeClosedDeals
                  error={form.errors.deal}
                  hideSearchIcon
                  disabled={!editable}
                />
              </Box>
            )}
            <Box
              width="full"
              flexDirection="row"
              gap="s16"
              paddingBottom="s16"
              style={{
                width: '50%',
                paddingRight: '8px',
              }}
            >
              <CurrencySelector
                value={form.values.currency ?? ''}
                error={form.errors.currency}
                onChange={(value) => form.setValues({ ...form.values, currency: value })}
                isDisabled={!editable}
              />
            </Box>
            <Box gap="s16" paddingTop="s16" paddingBottom="s16">
              <Box width="full" gap="s16" flexDirection="column">
                <SectionTitle
                  title={i18n.t('finance.sale_invoices.form.customize_title')}
                  description={i18n.t('finance.quotes.form.customize_description')}
                />
              </Box>
              <Box width="full">
                <TextArea
                  label={i18n.t('finance.sale_invoices.form.memo')}
                  onChange={(value) => {
                    if (typeof value == 'string') form.setValues({ ...form.values, memo: value })
                  }}
                  placeholder={i18n.t('finance.sale_invoices.form.text_area_placeholder')}
                  value={form.values.memo || ''}
                  readonly={!editable}
                />
              </Box>
              <Box width="full">
                <TextArea
                  label={i18n.t('finance.sale_invoices.form.footer')}
                  onChange={(value) => {
                    if (typeof value == 'string') form.setValues({ ...form.values, footer: value })
                  }}
                  placeholder={i18n.t('finance.sale_invoices.form.text_area_placeholder')}
                  value={form.values.footer || ''}
                  readonly={!editable}
                />
              </Box>
            </Box>
            {editable && (
              <Box flexDirection="rowReverse">
                <Button loading={form.isSubmitting} onClick={form.submitForm} type="primary">
                  {i18n.t('finance.quotes.actions.save')}
                </Button>
              </Box>
            )}
          </Form>
        </FormikProvider>
      </TwoColumnLayout>
    </ResponsivePage.Body>
  )
}

export default QuoteDetailsPage
